import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Modal, Button, Spin, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COPY TO CLIPBOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShield, faCopy, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { updateOpenModalConfirmResetOfflinePsw } from "../../../redux/users/userSlice";
import { usePostUserPswMutation } from "../../../redux/login/loginAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";

function UsersConfirmResetOfflinePswModal() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [generatedPassword, setGeneratedPassword] = useState<string>("");
	const [copied, setCopied] = useState(false);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { user, openModalConfirmResetOfflinePsw } = useSelector((state: RootState) => state.user);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [triggerPostUserPsw, { isLoading, isError, isSuccess, error, reset: resetPost }] =
		usePostUserPswMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const handleClose = () => {
		dispatch(updateOpenModalConfirmResetOfflinePsw(false));
	};

	const handleGenerate = async () => {
		const token = await GETJwtToken();
		const result: any = await triggerPostUserPsw({
			token,
			body: { user_id: user.id },
		});
		if (result?.data?.user_password) {
			setGeneratedPassword(result.data.user_password);
		}
	};

	const onCopy = () => {
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	React.useEffect(() => {
		if (openModalConfirmResetOfflinePsw) {
			resetPost();
			setGeneratedPassword("");
			setCopied(false);
		}
	}, [openModalConfirmResetOfflinePsw]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Modal
			onOk={handleClose}
			closable={false}
			onCancel={handleClose}
			open={openModalConfirmResetOfflinePsw}
			footer={null}
		>
			<Spin spinning={isLoading}>
				<div className="text-center">
					<FontAwesomeIcon
						icon={generatedPassword ? faCheckCircle : faShield}
						className={generatedPassword ? "generalStyles__success generalStyles__deleteItemIcon" : "generalStyles__warning generalStyles__deleteItemIcon"}
					/>
					{!generatedPassword ? (
						<>
							<p className="generalStyles__info mt-3">
								{t("userManagement.alertResetPassword")}
							</p>
							<p style={{ fontWeight: "bold" }}>{user.email}</p>
							<div className="generalStyles__flexCenter mt-4">
								<div className="generalStyles__width100">
									<Button onClick={handleClose} className="buttonStyle__29 generalStyles__width100">
										{t("general.cancel")}
									</Button>
								</div>
								<div className="generalStyles__mlFix generalStyles__width100">
									<Button onClick={handleGenerate} className="buttonStyle__28 generalStyles__width100">
										{t("userManagement.generateNewPassword")}
									</Button>
								</div>
							</div>
						</>
					) : (
						<>
							<p className="generalStyles__info mt-3">
								{t("general.offlinePassword")}
							</p>
							<div className="mt-3">
								<div className="generalStyles__flexCenter mt-4">
									<div className="generalStyles__mrFix generalStyles__width100">
										<Button onClick={handleClose} className="buttonStyle__29 generalStyles__width100">
											{t("general.close")}
										</Button>
									</div>
									<div className="generalStyles__width100">
										<CopyToClipboard onCopy={onCopy} text={generatedPassword}>
											<Tooltip title={copied ? "Copied!" : generatedPassword}>
												<Button
													className="buttonStyle__28 generalStyles__width100"
													icon={
														<FontAwesomeIcon
															icon={faCopy}
															className="generalStyles__mrFix"
														/>
													}
												>
													{generatedPassword.length > 10
														? `${generatedPassword.substring(0, 10)}...`
														: generatedPassword}
												</Button>
											</Tooltip>
										</CopyToClipboard>
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			</Spin>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isError}
				isSuccess={isSuccess}
				requestType='POST'
				error={error}
				name='Offline Password'
			/>
		</Modal>
	);
}

export default UsersConfirmResetOfflinePswModal;
